import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import './RafflePage.css';

interface Raffle {
  id: string;
  title: string;
  description: string;
  type: 'numbers' | 'names' | 'animals' | 'custom';
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  total_spots: number;
  spot_price: number;
  prize_description: string;
  draw_date: string;
  creator_id: string;
  winner_numbers?: number[];
  winner_user_id?: string;
  created_at: string;
  updated_at: string;
  creator?: {
    full_name: string;
    email: string;
  };
}

interface Participation {
  id: string;
  raffle_id: string;
  user_id: string;
  spot_numbers: number[];
  payment_status: 'pending' | 'paid' | 'cancelled' | 'approved' | 'rejected';
  reserved_at: string;
  paid_at?: string;
}

export const RafflePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [raffle, setRaffle] = useState<Raffle | null>(null);
  const [participations, setParticipations] = useState<Participation[]>([]);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [takenNumbers, setTakenNumbers] = useState<number[]>([]);
  const [reservedNumbers, setReservedNumbers] = useState<number[]>([]);

  useEffect(() => {
    if (id) {
      fetchRaffleData();
      subscribeToUpdates();
    }
  }, [id]);

  const fetchRaffleData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch raffle details
      const { data: raffleData, error: raffleError } = await supabase
        .from('raffles')
        .select('*')
        .eq('id', id)
        .single();

      if (raffleError) throw raffleError;
      if (!raffleData) throw new Error('Raffle not found');

      // Fetch creator info separately
      const { data: creatorData, error: creatorError } = await supabase
        .from('profiles')
        .select('full_name, email')
        .eq('id', raffleData.creator_id)
        .single();

      // Combine the data
      const raffleWithCreator = {
        ...raffleData,
        creator: creatorData || { full_name: 'N/A', email: '' }
      };

      setRaffle(raffleWithCreator);

      // Fetch all participations for this raffle
      const { data: participationsData, error: participationsError } = await supabase
        .from('participations')
        .select('*')
        .eq('raffle_id', id);

      if (participationsError) throw participationsError;

      setParticipations(participationsData || []);

      // Extract taken and reserved numbers
      const taken: number[] = [];
      const reserved: number[] = [];
      
      participationsData?.forEach(p => {
        if (p.payment_status === 'approved' || p.payment_status === 'paid') {
          taken.push(...p.spot_numbers);
        } else if (p.payment_status === 'pending') {
          // Check if reservation is still valid (30 minutes)
          const reservedTime = new Date(p.reserved_at).getTime();
          const now = new Date().getTime();
          const thirtyMinutes = 30 * 60 * 1000;
          
          if (now - reservedTime < thirtyMinutes) {
            reserved.push(...p.spot_numbers);
          }
        }
      });

      setTakenNumbers(taken);
      setReservedNumbers(reserved);
    } catch (err: any) {
      console.error('Error fetching raffle:', err);
      setError(err.message || 'Failed to load raffle');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToUpdates = () => {
    if (!id) return;

    // Subscribe to raffle updates
    const raffleSubscription = supabase
      .channel(`raffle-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'raffles',
          filter: `id=eq.${id}`
        },
        (payload) => {
          console.log('Raffle update:', payload);
          if (payload.eventType === 'UPDATE') {
            setRaffle(prev => prev ? { ...prev, ...payload.new } : null);
          }
        }
      )
      .subscribe();

    // Subscribe to participation updates
    const participationSubscription = supabase
      .channel(`participation-${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'raffle_participations',
          filter: `raffle_id=eq.${id}`
        },
        (payload) => {
          console.log('Participation update:', payload);
          fetchRaffleData(); // Refresh all data
        }
      )
      .subscribe();

    return () => {
      raffleSubscription.unsubscribe();
      participationSubscription.unsubscribe();
    };
  };

  const handleNumberClick = (number: number) => {
    if (takenNumbers.includes(number) || reservedNumbers.includes(number)) {
      return; // Number is not available
    }

    setSelectedNumbers(prev => {
      if (prev.includes(number)) {
        return prev.filter(n => n !== number);
      } else {
        return [...prev, number];
      }
    });
  };

  const handleParticipate = () => {
    if (selectedNumbers.length === 0) {
      alert('Please select at least one number');
      return;
    }

    // Navigate to participation flow
    navigate(`/raffle/${id}/participate`, {
      state: { selectedNumbers, raffle }
    });
  };

  const getNumberStatus = (number: number): 'available' | 'taken' | 'reserved' | 'selected' => {
    if (takenNumbers.includes(number)) return 'taken';
    if (reservedNumbers.includes(number)) return 'reserved';
    if (selectedNumbers.includes(number)) return 'selected';
    return 'available';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calculateProgress = () => {
    if (!raffle) return 0;
    return (takenNumbers.length / raffle.total_spots) * 100;
  };

  const calculateTotalRevenue = () => {
    if (!raffle) return 0;
    return takenNumbers.length * raffle.spot_price;
  };

  // useEffect deve ser chamado antes de qualquer return condicional
  useEffect(() => {
    if (raffle && raffle.status === 'draft' && !loading) {
      if (user && user.id === raffle.creator_id) {
        // Redirecionar para preview se for o criador
        navigate(`/raffle/${id}/preview`);
      }
    }
  }, [raffle, user, id, navigate, loading]);

  if (loading) {
    return (
      <div className="raffle-page-loading">
        <div className="spinner"></div>
        <p>Loading raffle...</p>
      </div>
    );
  }

  if (error || !raffle) {
    return (
      <div className="raffle-page-error">
        <h2>Error</h2>
        <p>{error || 'Raffle not found'}</p>
        <button onClick={() => navigate('/')}>Go Home</button>
      </div>
    );
  }

  if (raffle && raffle.status === 'draft') {
    if (user && user.id === raffle.creator_id) {
      // Retornar null enquanto redireciona
      return null;
    } else {
      // Se não for o criador, mostrar mensagem de indisponível
      return (
        <div className="raffle-page-unavailable">
          <h2>Rifa Indisponível</h2>
          <p>Esta rifa ainda não foi ativada.</p>
          <button onClick={() => navigate('/')}>Voltar ao Início</button>
        </div>
      );
    }
  }

  if (raffle.status === 'cancelled') {
    return (
      <div className="raffle-page-unavailable">
        <h2>Rifa Cancelada</h2>
        <p>Esta rifa foi cancelada pelo organizador.</p>
        <button onClick={() => navigate('/')}>Voltar ao Início</button>
      </div>
    );
  }

  return (
    <div className="raffle-page">
      <div className="raffle-header">
        <h1>{raffle.title}</h1>
        <p className="raffle-description">{raffle.description}</p>
        
        <div className="raffle-info-grid">
          <div className="info-card">
            <span className="info-label">Prize</span>
            <span className="info-value">{raffle.prize_description}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Price per Number</span>
            <span className="info-value">{formatCurrency(raffle.spot_price)}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Draw Date</span>
            <span className="info-value">{formatDate(raffle.draw_date)}</span>
          </div>
          <div className="info-card">
            <span className="info-label">Organizer</span>
            <span className="info-value">{raffle.creator?.full_name || 'Unknown'}</span>
          </div>
        </div>

        <div className="raffle-progress">
          <div className="progress-info">
            <span>Sold: {takenNumbers.length} / {raffle.total_spots}</span>
            <span>{Math.round(calculateProgress())}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${calculateProgress()}%` }}
            ></div>
          </div>
          <div className="progress-stats">
            <span>Total Revenue: {formatCurrency(calculateTotalRevenue())}</span>
          </div>
        </div>
      </div>

      {raffle.status === 'completed' && raffle.winner_numbers && (
        <div className="winner-announcement">
          <h2>🎉 Raffle Completed!</h2>
          <p>Winning Numbers: {raffle.winner_numbers.join(', ')}</p>
        </div>
      )}

      {raffle.status === 'active' && (
        <>
          <div className="numbers-section">
            <h2>Select Your Numbers</h2>
            <div className="legend">
              <div className="legend-item">
                <div className="legend-color available"></div>
                <span>Available</span>
              </div>
              <div className="legend-item">
                <div className="legend-color selected"></div>
                <span>Selected</span>
              </div>
              <div className="legend-item">
                <div className="legend-color reserved"></div>
                <span>Reserved</span>
              </div>
              <div className="legend-item">
                <div className="legend-color taken"></div>
                <span>Sold</span>
              </div>
            </div>

            <div className="numbers-grid">
              {Array.from({ length: raffle.total_spots }, (_, i) => i + 1).map(number => (
                <button
                  key={number}
                  className={`number-button ${getNumberStatus(number)}`}
                  onClick={() => handleNumberClick(number)}
                  disabled={getNumberStatus(number) === 'taken' || getNumberStatus(number) === 'reserved'}
                >
                  {number.toString().padStart(3, '0')}
                </button>
              ))}
            </div>
          </div>

          {selectedNumbers.length > 0 && (
            <div className="selection-summary">
              <h3>Your Selection</h3>
              <div className="selected-numbers">
                {selectedNumbers.sort((a, b) => a - b).map(num => (
                  <span key={num} className="selected-number">
                    {num.toString().padStart(3, '0')}
                  </span>
                ))}
              </div>
              <div className="selection-total">
                <span>Total: {selectedNumbers.length} numbers</span>
                <span className="total-price">
                  {formatCurrency(selectedNumbers.length * raffle.spot_price)}
                </span>
              </div>
              <button 
                className="participate-button"
                onClick={handleParticipate}
              >
                Continue to Payment
              </button>
            </div>
          )}
        </>
      )}

      <div className="raffle-footer">
        <button onClick={() => navigate('/')} className="back-button">
          Back to Home
        </button>
      </div>
    </div>
  );
};